import { Button } from '@mui/material'
import {
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js'
import dayjs from 'dayjs'
import { t } from 'i18next'
import React, { useEffect, useRef, useState } from 'react'
import { PriceVision } from '../../../models/bid/PriceVision'
import { colorPrimary } from '../../../rsc/colors/colors'
import { PeriodType } from '../../business/MillBusiness'

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend
)

interface ContractChartProps {
    priceVisions: PriceVision[]
    millId: number
    period: PeriodType
}

export const ContractChart: React.FC<ContractChartProps> = ({ millId, priceVisions, period }) => {

    const canvasRef = useRef<HTMLCanvasElement>(null)
    const chartInstanceRef = useRef<ChartJS | null>(null)

    useEffect(() => {
        if (priceVisions && priceVisions.length > 0) {
            createChart()
        }

        // Cleanup on unmount
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy()
            }
        }
    }, [priceVisions, period])

    const generateTimeRangeData = (): { labels: string[], dataMap: Map<string, PriceVision[]> } => {
        const now = dayjs()
        const labels: string[] = []
        const dataMap = new Map<string, PriceVision[]>()

        // Filter original data by mill if provided
        let filteredData = priceVisions.filter(vision => vision.market_day)

        switch (period) {
            case 'week':
                // Generate last 7 days
                for (let i = 6; i >= 0; i--) {
                    const date = now.subtract(i, 'days')
                    const dateKey = date.format('YYYY-MM-DD')
                    const label = date.format('MMM DD')
                    labels.push(label)

                    // Find data for this specific date
                    const dayData = filteredData.filter(vision =>
                        dayjs(vision.market_day).utc().format('YYYY-MM-DD') === dateKey
                    )
                    dataMap.set(label, dayData)
                }
                break

            case 'month':
                // Generate last 30 days
                for (let i = 29; i >= 0; i--) {
                    const date = now.subtract(i, 'days')
                    const dateKey = date.format('YYYY-MM-DD')
                    const label = date.format('MMM DD')
                    labels.push(label)

                    const dayData = filteredData.filter(vision =>
                        dayjs(vision.market_day).utc().format('YYYY-MM-DD') === dateKey
                    )
                    dataMap.set(label, dayData)
                }
                break

            case 'year':
                // Generate last 12 months
                for (let i = 11; i >= 0; i--) {
                    const date = now.subtract(i, 'months')
                    const monthKey = date.format('YYYY-MM')
                    const label = date.format('MMM YYYY')
                    labels.push(label)

                    const monthData = filteredData.filter(vision =>
                        dayjs(vision.market_day).utc().format('YYYY-MM') === monthKey
                    )
                    dataMap.set(label, monthData)
                }
                break
        }
        return { labels, dataMap }
    }

    const createChart = () => {
        const canvas = canvasRef.current
        if (!canvas) return
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy()
        }
        const { labels, dataMap } = generateTimeRangeData()
        const numContractsMap = new Map<number, (number | null)[]>()
        const numSuppliersMap = new Map<number, (number | null)[]>()
        labels.forEach((label, index) => {
            const dayData = dataMap.get(label) || []
            if (dayData.length === 0) {
                numContractsMap.forEach((values, millId) => {
                    values[index] = null
                })
                numSuppliersMap.forEach((values, millId) => {
                    values[index] = null
                })
            } else {
                dayData.forEach(vision => {
                    if (!millId) return
                    if (!numContractsMap.has(millId)) {
                        numContractsMap.set(millId, new Array(labels.length).fill(null))
                        numSuppliersMap.set(millId, new Array(labels.length).fill(null))
                    }
                    const existingValues = dayData
                    if (!existingValues) return
                    const numContracts = existingValues.reduce((acc, v) => acc + (v.numContracts ?? 0), 0) ?? null
                    const numSuppliers = existingValues.reduce((acc, v) => acc + (v.numSuppliers ?? 0), 0) ?? null
                    numContractsMap.get(millId)![index] = numContracts > 0 ? numContracts : null
                    numSuppliersMap.get(millId)![index] = numSuppliers > 0 ? numSuppliers : null
                })
            }
        })
        const datasets: any[] = []
        const colors = [
            'rgb(255, 99, 132)',
            'rgb(54, 162, 235)',
            'rgb(255, 205, 86)',
            'rgb(75, 192, 192)',
            'rgb(153, 102, 255)'
        ]
        Array.from(numContractsMap.entries()).forEach(([millId, data], index) => {
            datasets.push({
                label: t('contracts'),
                data: data,
                borderColor: colors[0],
                backgroundColor: colors[0],
                tension: 0.4,
                spanGaps: true,
                yAxisID: 'y',
            })
        })
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        chartInstanceRef.current = new ChartJS(ctx, {
            type: 'bar',
            data: {
                labels: labels,
                datasets
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top',
                    }
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1
                        },
                        title: {
                            display: true,
                            text: t('contracts') || ''
                        }
                    },
                    x: {
                        title: {
                            display: true,
                            text: t('date') || ''
                        }
                    }
                },
            }
        })
    }

    const getPeriodButtonStyle = (buttonPeriod: PeriodType): React.CSSProperties => {
        return {
            padding: '8px 16px',
            margin: '2px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: period === buttonPeriod ? colorPrimary() : 'white',
            color: period === buttonPeriod ? 'white' : colorPrimary(),
            cursor: 'pointer',
            fontSize: '12px',
            height: '30px'
        }
    }
    return (
        <div style={{ width: '100%', marginTop: '20px' }}>
            <div className='pempem-flex-row-rev'>
                <div>
                    <h3 className='bold'>{t('chart_contract_title')}</h3>
                </div>
            </div>
            <div style={{ height: '400px' }}>
                <canvas ref={canvasRef} />
            </div>
        </div>
    )
}